import React, { useState } from "react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { a11yDark } from "react-syntax-highlighter/dist/esm/styles/hljs";
import Panel from "../../common/components/panel";
import { useSavedFilterBruteForceCheckQuery } from "../../graphql/generated";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";


const BruteForceCheck: React.FC<{ savedFilterId?: string }> = ({
  savedFilterId,
}) => {
  const [listingId, setListingId] = useState("");
  const [submittedListingId, setSubmittedListingId] = useState<string | null>(
    null
  );

  const [{ data, fetching, error }] = useSavedFilterBruteForceCheckQuery({
    variables: {
      savedFilterId: savedFilterId ?? "",
      listingId: submittedListingId ?? "",
    },
    pause: !savedFilterId || !submittedListingId,
    requestPolicy: "network-only",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (listingId.trim()) {
      setSubmittedListingId(listingId.trim());
    }
  };

  const result = data?.savedFilterBruteForceCheck;

  return (
    <Panel>
      <Panel.Title>(EXPERIMENTAL) Listing Match Check</Panel.Title>
      <Panel.Body>
        <form onSubmit={handleSubmit} className="flex items-end gap-4">
          <div className="flex-1">
            <label
              htmlFor="listing-id"
              className="block text-sm font-medium text-gray-700"
            >
              Listing ID
            </label>
            <input
              type="text"
              id="listing-id"
              placeholder="e.g. mred:123456789"
              value={listingId}
              onChange={(e) => setListingId(e.target.value)}
              className="mt-1 block w-full shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-gray-300 rounded-md"
            />
          </div>
          <button
            type="submit"
            disabled={!listingId.trim() || fetching}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {fetching ? "Checking..." : "Check"}
          </button>
        </form>

        {fetching && (
          <p className="mt-4 text-sm text-gray-500">
            Running brute force check...
          </p>
        )}

        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-4">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  {error.graphQLErrors?.[0]?.message ?? error.message}
                </p>
              </div>
            </div>
          </div>
        )}

        {result && !fetching && (
          <div className="mt-4">
            {result.matches && (
              <div className="rounded-md bg-green-50 p-4">
                <div className="flex">
                  <CheckCircleIcon className="h-5 w-5 text-green-400" />
                  <p className="ml-3 text-sm font-medium text-green-800">
                    The listing matches this saved filter.
                  </p>
                </div>
              </div>
            )}

            {!result.matches && result.failingClauses.length === 0 && (
                <div className="rounded-md bg-yellow-50 p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                    <p className="ml-3 text-sm font-medium text-yellow-800">
                      The listing does not match, but no single clause removal
                      fixes it. Multiple clauses may be jointly responsible.
                    </p>
                  </div>
                </div>
              )}

            {!result.matches && result.failingClauses.length > 0 && (
              <div>
                <div className="rounded-md bg-red-50 p-4 mb-4">
                  <div className="flex">
                    <XCircleIcon className="h-5 w-5 text-red-400" />
                    <p className="ml-3 text-sm font-medium text-red-800">
                      The listing does not match. {result.failingClauses.length}{" "}
                      clause(s) cause the mismatch:
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  {result.failingClauses.map((fc, i) => (
                    <div
                      key={i}
                      className="border border-red-200 rounded-md overflow-hidden"
                    >
                      <div className="bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
                        bool.{fc.clauseType}[{fc.index}]
                      </div>
                      <SyntaxHighlighter language="json" style={a11yDark}>
                        {JSON.stringify(fc.clause, null, 2)}
                      </SyntaxHighlighter>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Panel.Body>
    </Panel>
  );
};

export default BruteForceCheck;
